/**
 * 共用表單驗證規則
 */
import type { RuleObject } from 'antd/es/form';

/**
 * IPv4 地址驗證器
 * 格式: x.x.x.x，每段 0-255
 */
export const ipv4Validator = (_: RuleObject, value: string) => {
  if (!value) return Promise.resolve();
  const pattern = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
  const match = value.match(pattern);
  if (!match) {
    return Promise.reject(new Error('請輸入有效的 IPv4 地址（如 192.168.1.1）'));
  }
  for (let i = 1; i <= 4; i++) {
    const octet = parseInt(match[i], 10);
    if (octet < 0 || octet > 255) {
      return Promise.reject(new Error('IP 地址每段必須在 0-255 之間'));
    }
  }
  return Promise.resolve();
};

/**
 * 節點連線地址驗證器：允許 IPv4、IPv6、FQDN。
 * 欄位仍送後端既有 `ip`，但實務上可代表節點 IP 或連線域名。
 */
export const endpointHostValidator = (_: RuleObject, value: string) => {
  if (!value) return Promise.resolve();
  const host = value.trim();
  const ipv4Pattern = /^(\d{1,3}\.){3}\d{1,3}$/;
  if (ipv4Pattern.test(host)) {
    return ipv4Validator(_, host);
  }

  const ipv6Pattern = /^[0-9a-f:]+$/i;
  if (host.includes(':') && ipv6Pattern.test(host)) {
    return Promise.resolve();
  }

  const hostnamePattern =
    /^(?=.{1,253}$)(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))*\.?$/i;
  if (hostnamePattern.test(host)) {
    return Promise.resolve();
  }

  return Promise.reject(new Error('請輸入有效 IP 或域名'));
};

/**
 * UUID v4 驗證規則（pattern）
 */
export const uuidV4Rule: RuleObject = {
  pattern: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  message: '請輸入有效的 UUID 格式（如 xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx）',
};

/**
 * Semver 版本號驗證規則（pattern）
 */
export const semverRule: RuleObject = {
  pattern: /^\d+\.\d+\.\d+$/,
  message: '請輸入有效的版本號格式（如 1.0.0）',
};

/**
 * 密碼強度驗證器
 * 至少 8 字元，包含大寫、小寫、數字
 */
export const passwordStrengthValidator = (_: RuleObject, value: string) => {
  if (!value) return Promise.resolve();
  if (value.length < 8) {
    return Promise.reject(new Error('密碼至少需要 8 個字元'));
  }
  if (!/[a-z]/.test(value)) {
    return Promise.reject(new Error('密碼需包含至少一個小寫字母'));
  }
  if (!/[A-Z]/.test(value)) {
    return Promise.reject(new Error('密碼需包含至少一個大寫字母'));
  }
  if (!/\d/.test(value)) {
    return Promise.reject(new Error('密碼需包含至少一個數字'));
  }
  return Promise.resolve();
};

/**
 * HTTPS URL 驗證規則（pattern）
 */
export const httpsUrlRule: RuleObject = {
  pattern: /^https:\/\/.+/,
  message: '請輸入有效的 HTTPS URL（如 https://example.com）',
};
